import * as React from 'react'

import { stringifyError } from '@ally/ally-login'
import { Box } from '@ally/metronome-ui'

import { useTransmit } from '../../TransmitContext'
import log from '../../whisper'
import { ErrorBanner, ErrorBannerProps } from '../ErrorBanner'
import { GuestEnrollExistingEmailBanner } from './CustomCIAMErrorBanners'

interface CIAMErrorBannerProps extends Pick<ErrorBannerProps, 'mb'> {
  error: Error | string | undefined
  className?: string
}

const CIAMErrorBanner: React.FC<CIAMErrorBannerProps> = ({
  error,
  mb = 'sm',
  className,
}) => {
  let errorMessage = "Something's not working. Try again later."
  // If you return a customErrorComponent it will render instead of the
  // default ErrorBanner. You will be responsible for handling any
  // necessary analytics within the custom component
  let customErrorComponent: React.ReactElement | undefined

  switch (error) {
    case 'CIAM_E08_07_001':
    case 'CIAM_E08_07_002':
      errorMessage = "Hmm. We're having trouble finding you. Try again."
      break
    case 'CIAM_E01_03_014':
      errorMessage = "The SSN you entered doesn't match what we have on file."
      break
    case 'CIAM_E08_01_010':
      errorMessage = 'One more try to enter a valid SSN.'
      break
    case 'CIAM_E08_00_006':
      errorMessage = 'SSN or password retry limit exceeded.'
      break
    case 'CIAM_E01_02_012':
      errorMessage = "You've recently used that password. Try again."
      break
    case 'CIAM_E01_03_001':
      errorMessage =
        'The username and SSN combination you entered is not valid. Please try again.'
      break
    case 'CIAM_E08_01_002': // Invalid username: username contains forbidden characters
      // Use default error message, this should be caught by the frontend validations
      break
    case 'CIAM_E08_02_001':
      errorMessage = 'Username already exists'
      break
    case 'CIAM_E08_02_007':
      errorMessage =
        'Your Auto account is locked. Give us a call to finish linking and access your account.'
      break
    case 'CIAM_E08_02_008':
      errorMessage = 'The username and password you entered aren’t valid.'
      break
    case 'CIAM_E08_00_013':
      // This is a custom error banner and will handle the analytics call in the component
      customErrorComponent = <GuestEnrollExistingEmailBanner error={error} />
      break
    case 'CIAM_E08_01_007':
      errorMessage = 'Please enter a valid email address'
      break
    case 'CIAM_E08_08_009':
      errorMessage =
        'Make sure the ZIP code you entered is the one associated with your billing address.'
      break
    case 'CIAM_E08_08_010':
      errorMessage =
        'Enter the ZIP code associated with your billing address. You have one attempt left.'
      break
    case 'CIAM_E04_03_025': // this is the same code for bad password & password hint
    case 'CIAM_E08_01_001': // invalid input length (got for secret question, secret answer when empty strings)
      // Displaying default error message for the above cases
      break
    case 'CIAM_E01_08_001': // AAOS_ACCOUNT_VERIFICATION_ERROR
      errorMessage =
        'We’re having trouble finding your account. Double-check what you entered or give us a call for help.'
      break
    case 'CIAM_E08_04_001': // CIAM_CUSTOMER_MISSING_ERROR
      errorMessage =
        "We can't find a user with that information. Double-check that all fields are entered correctly."
      break
    default:
      if (error) {
        log.error({
          message: [`Unknown error code returned from transmit: ${error}`],
        })
      }
  }

  const { hostServices } = useTransmit()

  React.useEffect(() => {
    if (error && !customErrorComponent) {
      hostServices?.analytics.trackPageView({
        eventType: 'customError',
        pageName: errorMessage,
        options: { error: stringifyError(error), isFormError: false },
      })
    }
  }, [error])

  if (!error) {
    return null
  }

  return (
    <Box className={className}>
      {customErrorComponent || (
        <ErrorBanner mb={mb}>{errorMessage}</ErrorBanner>
      )}
    </Box>
  )
}

export default CIAMErrorBanner
